import { useCallback, useEffect, useRef, useState } from 'react';

import createDOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify/unstyled';

import { useAppDispatch } from '@app/store/store';
import { setCredentials } from '@features/authentication/store/authentication.slice';
import { GenericButton, GenericModal } from '@shared/components/ui/custom';
import { LoadingPageSpinner } from '@shared/overlays';
import { notify } from '@shared/utils';

import {
  useAcceptUserAgreementMutation,
  useGetRefreshTokenMutation,
  useGetUserAgreementQuery,
} from '../../api/authentication.api';

const DOMPurify = createDOMPurify(globalThis);
const SCROLL_BOTTOM_THRESHOLD_PX = 5;

interface UserAgreementModalProps {
  onClose: () => void;
  tenantId: string;
  token: string;
  email: string;
}

export const UserAgreementModal = ({ onClose, tenantId, token, email }: UserAgreementModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const policyRef = useRef<HTMLDivElement>(null);

  const {
    data: userAgreementData,
    isLoading: loadingAgreement,
    isError: errorAgreement,
  } = useGetUserAgreementQuery({ tenantId, token });

  const [
    acceptAgreement,
    { isLoading: accepting, isError: errorAccept, data: acceptData, reset: resetUserAgreementMutation },
  ] = useAcceptUserAgreementMutation();

  // Deliberate second refresh path, intentionally NOT routed through the access-token-refresh Web Lock /
  // singleton used by the silent 401-recovery and cold-start refreshes. This is a one-shot,
  // user-triggered step of the login flow (fired only after the agreement is accepted, before the app
  // shell mounts), so it cannot race those silent refreshes: nothing else refreshes during login, and
  // a tab can't be authenticated elsewhere while its own agreement is still pending. It also needs the
  // exact token minted *after* acceptance (USER_AGREEMENT_REQUIRED flips to false) — feeding it through
  // the shared singleton could hand back a token from an in-flight pre-acceptance refresh.
  const [
    refreshToken,
    { isSuccess: refreshSuccess, isError: refreshError, data: refreshData, reset: resetRefreshTokenMutation },
  ] = useGetRefreshTokenMutation();

  const sanitizedHtml = userAgreementData?.policy ? DOMPurify.sanitize(userAgreementData.policy) : '';

  const handleOnClose = useCallback(() => {
    onClose();
    resetUserAgreementMutation?.();
    resetRefreshTokenMutation?.();
  }, [onClose, resetUserAgreementMutation, resetRefreshTokenMutation]);

  useEffect(() => {
    if (errorAgreement) toast.error('Error fetching user agreement');
  }, [errorAgreement]);

  useEffect(() => {
    if (errorAccept) {
      toast.error('Error accepting agreement');
      return;
    }

    if (acceptData?.message === 'Agreement accepted!') {
      void refreshToken();
    }
  }, [acceptData, errorAccept, refreshToken]);

  useEffect(() => {
    if (refreshSuccess && refreshData?.token) {
      dispatch(setCredentials({ username: email, token: refreshData.token, tenantId }));

      notify({
        type: 'success',
        title: 'User agreement accepted',
        description: 'Logged in.',
        toastId: 'agreement-accepted',
      });

      handleOnClose();
      void navigate('/');

      return;
    }

    if (refreshError) toast.error('Error refreshing token. Please try again.');
  }, [refreshSuccess, refreshError, refreshData, dispatch, email, navigate, handleOnClose, tenantId]);

  const handleAccept = () => {
    if (!userAgreementData?.id) return;

    void acceptAgreement({
      agreementId: userAgreementData.id,
      email,
      tenantId,
      token,
    });
  };

  const handleScroll = () => {
    const el = policyRef.current;
    if (!el) return;

    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX;
    if (atBottom) setHasScrolledToBottom(true);
  };

  if (loadingAgreement) return <LoadingPageSpinner />;
  if (!userAgreementData || loadingAgreement) return null;

  return (
    <GenericModal
      open
      onOpenChange={handleOnClose}
      title="Pilot Participation Agreement"
      description="Please review the agreement and scroll to the bottom before accepting."
      contentProps={{ className: 'max-w-4xl' }}
      data-testid="authentication-user-agreement-modal"
    >
      <div
        ref={policyRef}
        onScroll={handleScroll}
        className="max-h-128 overflow-y-auto p-4 border rounded-md bg-background mb-6 prose prose-sm dark:prose-invert"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        data-testid="authentication-user-agreement-scrollable"
      />

      <div className="flex justify-end" data-testid="authentication-user-agreement-actions">
        <GenericButton
          label="Accept"
          onClick={handleAccept}
          isLoading={accepting}
          disabled={!hasScrolledToBottom}
          data-testid="authentication-user-agreement-accept-button"
        />
      </div>
    </GenericModal>
  );
};
